import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";
import { isRecord, isString } from "generic-type-guard";

import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";
import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormActivity, FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { getDomIdForA11yId } from "../../../app/slices/accessibility/selectors";
import { ModelElements } from "../../../config";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { isWorkflowActivity, saveActivity } from "../../../utilities/activityUtils";
import { setFocusToHTMLElement } from "../../../utilities/domUtils";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { isJsonRpc2ResponseArray, printDocument } from "../../../utilities/rpcHelper";
import { executeWithoutValidation, weakValidation } from "../../../utilities/validationUtils";
import {
	DATEINAME,
	DATEINAME_FORMAT,
	DATEINAME_FORMAT_STAMMDATEN,
	PATH_TO_FILE_ATTACHMENT_LOCATION,
	PRINTMODEL_ID_NAME
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeBusy, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { GENERATE_PDF_EVENT } from "../../../utilities/variables/customEventNames";
import { isDocumentWithProcessMetadata } from "../../workflows/data/processMetadata";
import { extractErrorMessage } from "../../../utilities/error";
import { normalizePlatzhalterA12Path } from "../../../utilities/platzhalterHelper";
import { createEventButtonActionMatcher } from "../../../utilities/actionUtils";
import { StammdatenFormDescriptor } from "../../../utilities/descriptors";
import { isStammdatenDocument } from "../../stammdaten/types";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

import SingleDocumentData = FormActivity.Data.SingleDocumentData;

const logger = LoggerFactory.getLogger("modulf");

/**
 * Reagiert auf den Buttonklick zum PDF-Generieren und erstellt einen Request
 */
export function* generatePdfSaga(): SagaGenerator<void> {
	yield* takeLatest(createEventButtonActionMatcher(GENERATE_PDF_EVENT), handle);
}

function* handle(
	action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonTriggeredPayload>>>
): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));
	const stammdatenForm = StammdatenFormDescriptor.isInstance(activity?.descriptor);

	yield* call(executeBusy, function* (): SagaGenerator<void> {
		if (stammdatenForm) {
			yield* call(printAndUpdateDocument(action));
		} else {
			yield* call(executeWithoutValidation, action.payload.activityId, printAndUpdateDocument(action));
		}
	});

	const regeneratePDFButtonId = yield* select(getDomIdForA11yId(ModelElements.erneutGenerierenButton));

	if (!regeneratePDFButtonId) {
		return;
	}

	yield* call(setFocusToHTMLElement, regeneratePDFButtonId);
}

function printAndUpdateDocument(
	action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonTriggeredPayload>>>
): () => SagaGenerator<void> {
	return function* (): SagaGenerator<void> {
		const activityId = action.payload.activityId;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (!activity) {
			throw new Error("Couldn't find activity");
		}

		const group = `generatePdf:${activity.descriptor.instance}`;
		const isValid = yield* call(weakValidation, activity.id);
		if (!isValid) {
			logger.error("Document is not valid.");
			return;
		}

		const isSaved = yield* saveActivity(activityId);
		if (!isSaved) {
			yield* call(handleError, "Count not save activity " + activityId, group);
			return;
		}

		const data = yield* select(ActivitySelectors.data(activityId));
		if (!SingleDocumentData.isInstance(data)) {
			yield* call(handleError, "Could not find document data in " + activity.id, group);
			return;
		}

		// Button annotations holen (enthält Infos wie das zu verwendende Printmodel)
		const annotations = yield* select(
			ModulfUiSelectors.selectButtonAnnotations(activityId, action.payload.engineEvent.payload.buttonPath)
		);

		try {
			// Request schicken zum PDF generieren
			yield* call(generatePdf, data.document, annotations);

			yield* call(reloadActivity, activityId, activity);
		} catch (e) {
			yield* call(handleError, e, group);
		}
	};
}

export function* handleError(error: any, group: string) {
	logger.error("Fehler beim Generieren des Dokuments", error);
	let errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.GENERATE_DOCUMENT_ERROR);
	if (isJsonRpc2ResponseArray(error)) {
		const rootCause = error
			.filter(JsonRpc2Response.error.isInstance)
			.map(it => extractErrorMessage(it.error))
			?.find(it => it !== null);
		if (rootCause !== null) {
			errorMessage = localizableFromLocaleResources(RESOURCE_KEYS.error.GENERATE_DOCUMENT_WITH_CAUSE_ERROR, {
				cause: { type: "plain", value: rootCause }
			});
		}
	}
	yield* call(putErrorNotification, errorMessage, error, undefined, group);
}

function* reloadActivity(activityId: string, activity: Activity): SagaGenerator<void> {
	if (isWorkflowActivity(activity)) {
		yield* call(reloadTaskAndAntragActivity);
	} else {
		yield* put(ActivityActions.reloadData({ activityId }));
	}
}

/**
 * Sendet einen Request an den PrintService ein PDF zu generieren.
 * @param document
 * @param annotations
 */
export async function generatePdf(document: Activity.Data.Document, annotations: readonly Annotation[]): Promise<void> {
	let pathToFileAttachments = annotations?.find((a: Annotation) => a.name == PATH_TO_FILE_ATTACHMENT_LOCATION)?.value;
	// Attachment path enthält vor root noch Namen des Models, muss entfernt werden
	pathToFileAttachments = normalizePlatzhalterA12Path(pathToFileAttachments);

	const printModelValue = annotations.find(a => a.name === PRINTMODEL_ID_NAME)?.value;
	const printModelId = resolvePrintModelId(document, printModelValue);
	if (!printModelId) {
		return Promise.reject(new Error("No print model id given!"));
	}

	const dateiname_format = isStammdatenDocument(document)
		? annotations.find(a => a.name === DATEINAME_FORMAT_STAMMDATEN)?.value
		: annotations.find(a => a.name === DATEINAME_FORMAT)?.value;

	await printDocument(document.id, [
		{
			printModelId,
			pathToFileAttachments,
			attachmentDocumentType: annotations.find(a => a.name === DATEINAME)?.value,
			fileNameFormat: dateiname_format
		}
	]);
}

function resolvePrintModelId(document: Activity.Data.Document, printModelValue?: string): string | undefined {
	if (!printModelValue) {
		return undefined;
	}

	const printModelIdObj = isJsonObject(printModelValue) ? JSON.parse(printModelValue) : printModelValue;
	if (isString(printModelIdObj)) {
		return printModelValue;
	} else if (isRecord(printModelIdObj, isString)) {
		const client = isDocumentWithProcessMetadata(document)
			? (document.root.processMetadata.fachverfahren.client ?? "")
			: isStammdatenDocument(document)
				? (document.root.stammdatenDefault.stammdatenMetadata?.client ?? "")
				: "";
		return printModelIdObj[client];
	}

	return undefined;
}

function isJsonObject(str: string) {
	const trimmedStr = str.trim();
	return trimmedStr.startsWith("{") && trimmedStr.endsWith("}");
}
